// Plant wat de schakelaar 'Kwalificeringsvragen' op een vragenlijst moet
// schrijven: aanmaken, terughalen of zacht verwijderen. Puur — de schrijfstap
// (één batch, met de Firestore-specifieke velden voor terughalen en
// verwijderen) zit in `applyQualifyingToggle`.
//
// Herkent alles op sleutel (`QualifyingKey` op veld en sectie, `OptionKey` op
// optie), nooit op tekst. Wat al bestaat wordt hergebruikt, ook als het
// verwijderd is: zo houden de vragen bij opnieuw aanzetten hun `ID` en blijven
// eerdere antwoorden eraan hangen. Staat alles er al, dan is het plan leeg —
// een dubbelklik maakt dus geen tweede set.
// AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md

import notDeleted from '../notDeleted'

/**
 * @typedef {import('./sectorQualifyingQuestions').QualifyingSet} QualifyingSet
 */

/**
 * @typedef {object} PlannedCreate
 * @property {'QuestionnaireFields'|'MultipleQuestionOptions'} collection
 * @property {object} data - Het volledige nieuwe document
 */

/**
 * @typedef {object} PlannedRestore
 * @property {'QuestionnaireFields'|'MultipleQuestionOptions'} collection
 * @property {string} docid
 * @property {object} data - Velden die naast het opheffen van de verwijdering gezet worden
 */

/**
 * @typedef {object} PlannedSoftDelete
 * @property {'QuestionnaireFields'} collection
 * @property {string} docid
 */

/**
 * @typedef {object} QualifyingPlan
 * @property {PlannedCreate[]} creates
 * @property {PlannedRestore[]} restores
 * @property {PlannedSoftDelete[]} softDeletes
 * @property {string[]} questionFieldIds - ID's van de vragen (niet de sectie), in volgorde
 */

const FIELDS = 'QuestionnaireFields'
const OPTIONS = 'MultipleQuestionOptions'

// Merkkleuren, zodat ja en nee in elke grafiek dezelfde kleur hebben.
const OPTION_COLORS = { ja: '#47acc3', nee: '#f38284' }

/**
 * Eén plek boven het laagste levende veld op het hoogste niveau.
 *
 * @param {Array<object>} fields
 * @returns {number}
 */
const topPosition = (fields) => {
    const positions = fields
        .filter((f) => notDeleted(f) && !f.SectionID && !f.QualifyingKey)
        .map((f) => Number(f.Position))
        .filter(Number.isFinite)
    return Math.min(1, ...positions) - 1
}

/**
 * Het bestaande document met deze sleutel; een levend gaat voor een verwijderd.
 *
 * @param {Array<object>} docs
 * @param {(doc: object) => boolean} match
 * @returns {object | undefined}
 */
const findExisting = (docs, match) => {
    const matches = docs.filter(match)
    return matches.find(notDeleted) || matches[0]
}

/**
 * @param {object} params
 * @param {QualifyingSet} params.set - De set uit `sectorQualifyingQuestions`
 * @param {boolean} params.active - true = aanzetten, false = uitzetten
 * @param {Array<object>} params.fields - Alle QuestionnaireFields van de vragenlijst, ook verwijderde
 * @param {Array<object>} params.options - MultipleQuestionOptions van de kwalificeringsvragen, ook verwijderde
 * @param {string} params.questionnaireId
 * @param {string} params.client - CompagnyID
 * @param {*} params.timestamp - Wordt als `Timestamp` op nieuwe documenten gezet
 * @param {() => string} params.makeId - Maakt een nieuw ID (uuid)
 * @returns {QualifyingPlan}
 */
const buildQualifyingDocs = ({ set, active, fields, options, questionnaireId, client, timestamp, makeId }) => {
    /** @type {QualifyingPlan} */
    const plan = { creates: [], restores: [], softDeletes: [], questionFieldIds: [] }

    const sectionDoc = findExisting(fields, (f) => f.QualifyingKey === set.key)
    const questionDocs = set.questions.map((q) => findExisting(fields, (f) => f.QualifyingKey === q.key))

    if (!active) {
        const alive = [sectionDoc, ...questionDocs].filter((doc) => doc && notDeleted(doc))
        alive
            .forEach((doc) => plan.softDeletes.push({ collection: FIELDS, docid: doc.docid }))
        plan.questionFieldIds = questionDocs.filter(Boolean).map((doc) => doc.ID)
        return plan
    }

    const position = topPosition(fields)
    const owner = { Compagny: client, CompagnyID: client, Timestamp: timestamp }
    const fieldDefaults = {
        ...owner,
        QuestionnaireID: questionnaireId,
        Explainer: '',
        ReachStart: 0,
        ReachStartLable: '',
        ReachEnd: 5,
        ReachEndLabel: '',
        Multiple: [],
        LiveReportHidden: false,
    }

    let sectionId
    if (!sectionDoc) {
        sectionId = makeId()
        plan.creates.push({
            collection: FIELDS,
            data: {
                ...fieldDefaults,
                ID: sectionId,
                Key: makeId(),
                Type: 'section',
                Question: set.sectionTitle,
                Title: set.sectionTitle,
                Position: position,
                SectionID: '',
                QualifyingKey: set.key,
            },
        })
    } else {
        sectionId = sectionDoc.ID
        if (!notDeleted(sectionDoc)) {
            plan.restores.push({ collection: FIELDS, docid: sectionDoc.docid, data: { Position: position } })
        }
    }

    set.questions.forEach((question, index) => {
        const placement = { Required: true, SectionID: sectionId, SectionPosition: index + 1 }
        const existing = questionDocs[index]
        let fieldId

        if (!existing) {
            fieldId = makeId()
            plan.creates.push({
                collection: FIELDS,
                data: {
                    ...fieldDefaults,
                    ...placement,
                    ID: fieldId,
                    Key: makeId(),
                    Type: 'multiple-one',
                    Question: question.question,
                    Position: position,
                    QualifyingKey: question.key,
                },
            })
        } else {
            fieldId = existing.ID
            if (!notDeleted(existing)) {
                plan.restores.push({ collection: FIELDS, docid: existing.docid, data: placement })
            }
        }
        plan.questionFieldIds.push(fieldId)

        question.options.forEach((option, optionIndex) => {
            const existingOption = findExisting(options, (o) => o.Field === fieldId && o.OptionKey === option.key)

            if (!existingOption) {
                plan.creates.push({
                    collection: OPTIONS,
                    data: {
                        ...owner,
                        ID: makeId(),
                        Field: fieldId,
                        Option: option.label,
                        OptionKey: option.key,
                        Question: question.question,
                        Position: optionIndex + 1,
                        Color: OPTION_COLORS[option.key] || '',
                    },
                })
            } else if (!notDeleted(existingOption)) {
                plan.restores.push({ collection: OPTIONS, docid: existingOption.docid, data: {} })
            }
        })
    })

    return plan
}

export default buildQualifyingDocs
